import React, { useMemo } from 'react';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import { CTA, FeatureLinkItem } from 'types';
import './FeaturedElements.scss';

interface FeaturedElementsProps {
  featuredLink: FeatureLinkItem | undefined;
  l0Title: string;
  handleMouseEnterOnLinks: () => void;
  handleMouseLeaveOnLinks: () => void;
}

const FeaturedElements = ({
  featuredLink,
  l0Title,
  handleMouseEnterOnLinks,
  handleMouseLeaveOnLinks,
}: FeaturedElementsProps) => {
  const formatDataEm = (text: string) => {
    const replacedText = text.replace('Featured:', '');
    return `TopNav_Featured:${l0Title}_${replacedText.replace(/ /g, '')}`;
  };

  // filter out featured link data that's improperly formatted. Of particular importance is the
  // scenario where the `cta` field is an array with a single empty object: [{}]. This happens when
  // that data is unset
  const featuredLinks = useMemo(
    () =>
      featuredLink?.cta?.filter((cta: CTA | Record<string, never>): cta is CTA => {
        return (
          Object.keys(cta).length > 0 &&
          'linkTitle' in cta &&
          'link' in cta &&
          typeof cta.linkTitle === 'string' &&
          typeof cta.link === 'string'
        );
      }) ?? [],
    [featuredLink?.cta],
  );

  if (featuredLinks.length === 0) {
    return null;
  }

  return (
    <div className="featured-element-container hmf-pt-xs" id="featuredElementsContainer">
      {featuredLinks.map((cta, index) => (
        <ErrorBoundaryWithLogger componentId="FeaturedElements" key={JSON.stringify(cta)}>
          <a
            className="featured-element l0-link-content hmf-display-flex hmf-align-items-center hmf-px-s hmf-py-xxs title-color hmf-body-bold-l"
            id={`featured-element-${index}`}
            tabIndex={0}
            aria-label={`Open ${cta.linkTitle}`}
            href={cta.link}
            onMouseEnter={() => handleMouseEnterOnLinks()}
            onMouseLeave={() => handleMouseLeaveOnLinks()}
            data-em={formatDataEm(cta.linkTitle)}>
            {cta.linkTitle}
          </a>
        </ErrorBoundaryWithLogger>
      ))}
    </div>
  );
};

export default FeaturedElements;
